<template>
  <div
    v-tooltip="tooltip"
    class="
      tw-p-3
      tw-pb-[10px]
      tw-bg-sf-snow-lighter
      tw-flex-1
      tw-flex
      tw-flex-col
      tw-justify-items-center
      tw-items-center
      tw-border-b-2
      tw-border-t-0
      tw-border-r-0
      tw-border-l-0
      tw-border-solid
      tw-transition-all
    "
    :class="containerClass"
    :data-testid="`sidepane-tab-${type}`"
    @click.prevent="handleClick"
  >
    <div class="tw-flex tw-items-center" :class="iconContainerClass">
      <div class="tw-relative tw-mr-1">
        <i class="tw-text-lg tw-leading-[18px] fa" :class="iconClass" />
        <SfIndicatorNew
          v-if="shouldShowNew"
          :position="'top-left'"
          data-testid="sidepane-new-indicator"
        />
      </div>
      <SfIndicatorCount
        v-if="shouldShowCount"
        data-testid="sidepane-tab-count"
        :ai="ai"
      >
        {{ newItemCount }}
      </SfIndicatorCount>
    </div>
    <div
      v-if="!onlyTooltip"
      class="tw-text-sf-gray tw-text-sm tw-whitespace-nowrap"
      :class="textClass"
    >
      {{ text }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SidepaneFilterType } from './types'

import { computed } from 'vue'

import { SfIndicatorCount, SfIndicatorNew } from '../sf-indicator'

interface Props {
  disabled?: boolean
  icon: string
  newItemCount?: number
  onlyTooltip?: boolean
  selected?: boolean
  showNew?: boolean
  ai?: boolean
  text: string
  type: SidepaneFilterType
}

const {
  disabled,
  icon,
  onlyTooltip,
  newItemCount,
  selected,
  showNew,
  text,
  type,
} = defineProps<Props>()

const emit =
  defineEmits<{
    click: [type: SidepaneFilterType]
  }>()

function handleClick() {
  if (!disabled) {
    emit('click', type === 'all' ? null : type)
  }
}

const containerClass = computed(() => ({
  'tw-border-transparent': !selected || disabled,
  'tw-border-sf-primary': selected && !disabled,
  'tw-opacity-30': disabled,
  'tw-cursor-not-allowed': disabled,
  'hover:tw-bg-sf-neutral-dark-1': !disabled,
  'hover:tw-bg-opacity-10': !disabled,
  'hover:tw-border-sf-neutral-light-1': !disabled,
  'hover:tw-cursor-pointer': !disabled,
}))

const iconContainerClass = computed(() => ({
  'tw-h-full': onlyTooltip,
}))

const iconClass = computed(() => ({
  [`fa-${icon}`]: true,
  'tw-text-sf-gray-dark': selected && !disabled,
  'tw-text-sf-neutral-light-2': !selected,
}))

const textClass = computed(() => ({
  'tw-font-normal': !selected,
  'tw-font-medium': selected && !disabled,
}))

const shouldShowCount = computed(() => newItemCount && !disabled)

const shouldShowNew = computed(() => showNew && !disabled)

const tooltip = computed(() => (onlyTooltip ? text : ''))
</script>
